import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Snowfall } from "@/components/Snowfall";
import { CountdownTimer } from "@/components/CountdownTimer";
import { CallPreview } from "@/components/CallPreview";
import { TrustBadges } from "@/components/TrustBadges";
import { SocialProof } from "@/components/SocialProof";
import { TopNav } from "@/components/TopNav";
import { VideoPreview } from "@/components/previews/VideoPreview";
import { AnimatedSanta, AnimatedTree, AnimatedGift, AnimatedStar } from "@/components/icons/ChristmasIcons";
import { AuroraBorealis } from "@/components/animations/AuroraBorealis";
import { FloatingElements, MagicParticles } from "@/components/animations/FloatingElements";
import { SEO } from "@/components/SEO";
import { 
  Gift, 
  Sparkles, 
  Phone, 
  Star,
  Users,
  Calendar,
  Mail,
  BookOpen,
  Camera,
  Award,
  ClipboardList,
  Check,
  TreePine,
  HelpCircle,
  Heart,
  ArrowRight,
  Layers,
  Wand2,
  Clock,
  Video,
  Play
} from "lucide-react";
import { Link } from "react-router-dom";

const freeFeatures = [
  {
    icon: ClipboardList,
    title: "Wishlist Builder",
    description: "Track gift ideas for each child with notes",
  },
  {
    icon: Users,
    title: "Secret Santa",
    description: "Organize gift exchanges with email notifications",
  },
  {
    icon: Star,
    title: "Naughty & Nice",
    description: "Track behavior with fun Santa-style monitoring",
  },
  {
    icon: Award,
    title: "Certificates",
    description: "Generate official Nice List certificates",
  },
];

const magicPassFeatures = [
  {
    icon: Calendar,
    title: "Elf Planner",
    description: "AI-generated Elf on the Shelf ideas",
  },
  {
    icon: BookOpen,
    title: "Christmas Stories",
    description: "AI-generated magical stories for children",
  },
  {
    icon: Mail,
    title: "Letter to Santa",
    description: "Send letters and receive AI-powered replies",
  },
  {
    icon: Camera,
    title: "Photo Creator",
    description: "AI-powered Christmas photo transformations",
  },
];

const ultimatePassFeatures = [
  {
    icon: Phone,
    title: "Call Santa AI",
    description: "Real voice calls with Santa powered by AI magic",
    highlight: true,
  },
  {
    icon: Video,
    title: "Santa Video Messages",
    description: "3 personalized AI video messages from Santa included",
    highlight: true,
  },
  ...magicPassFeatures,
];

const pricingTiers = [
  {
    name: "Free Plan",
    price: "£0",
    description: "Perfect for getting started",
    duration: "Forever free",
    features: ["Dashboard", "Presents List", "Secret Santa", "Naughty & Nice Tracker", "Certificates"],
    tier: "free",
  },
  {
    name: "Christmas Magic Pass",
    price: "£3.99",
    description: "Full Christmas experience",
    duration: "60 days access",
    features: ["All Free features", "Elf Planner with AI ideas", "AI Christmas Stories", "Letter to Santa with AI replies", "Photo Creator", "3 Credits (Videos & Photos)"],
    tier: "magic",
    popular: true,
  },
  {
    name: "Santa Ultimate Pass",
    price: "£9.99",
    description: "The complete experience",
    duration: "60 days access",
    features: ["All Magic Pass features", "Unlimited Call Santa AI", "5 Credits (Videos & Photos)", "Additional credits available"],
    tier: "ultimate",
    highlight: true,
  },
];

const Index = () => {
  return (
    <div className="min-h-screen bg-background relative overflow-hidden max-w-[100vw]">
      <SEO 
        title="Let Your Child Talk to Santa AI - Magical Christmas Experience"
        description="Create magical Christmas memories! Let your child call and talk to Santa AI. Personalized conversations, stories, letters, videos & photos from just £3.99."
        path="/"
      />
      <Snowfall />
      
      <main>
      
      {/* Premium Aurora Background */}
      <AuroraBorealis />
      
      {/* Floating Elements */}
      <FloatingElements variant="hero" />
      
      {/* Magic Particles */}
      <MagicParticles />
      
      {/* Enhanced Background with Pattern & Gradients */}
      <div className="fixed inset-0 pointer-events-none">
        {/* Animated gradient orbs - hidden on mobile for performance */}
        <div className="hidden sm:block absolute top-0 left-1/4 w-[400px] md:w-[600px] h-[400px] md:h-[600px] bg-primary/10 rounded-full blur-[100px] md:blur-[120px] animate-pulse" style={{ animationDuration: '8s' }} />
        <div className="hidden sm:block absolute bottom-1/4 right-1/4 w-[500px] md:w-[700px] h-[500px] md:h-[700px] bg-gold/8 rounded-full blur-[120px] md:blur-[140px] animate-pulse" style={{ animationDuration: '10s', animationDelay: '2s' }} />
        <div className="hidden sm:block absolute top-1/2 left-0 w-[350px] md:w-[500px] h-[350px] md:h-[500px] bg-accent/8 rounded-full blur-[80px] md:blur-[100px] animate-pulse" style={{ animationDuration: '12s', animationDelay: '4s' }} />
        
        {/* Dot pattern overlay */}
        <div className="absolute inset-0 opacity-20 sm:opacity-30" style={{
          backgroundImage: 'radial-gradient(hsl(var(--primary) / 0.15) 1px, transparent 1px)',
          backgroundSize: '24px 24px sm:32px sm:32px'
        }} />
        
        {/* Top gradient wash */}
        <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_top,hsl(348_70%_38%/0.15),transparent_60%)]" />
        <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_bottom_right,hsl(42_75%_48%/0.12),transparent_60%)]" />
      </div>
      
      {/* Top Navigation */}
      <TopNav />

      {/* Hero Section with WOW Factor */}
      <section className="relative z-10 pt-24 pb-12 sm:pt-32 sm:pb-20 md:pt-40 md:pb-28 px-4">
        <div className="container max-w-6xl mx-auto">
          <div className="grid lg:grid-cols-2 gap-8 lg:gap-16 items-center">
            <div className="text-center lg:text-left">
              {/* Animated Santa Badge */}
              <div className="inline-flex items-center gap-2 sm:gap-3 bg-gradient-to-r from-primary/15 to-gold/10 text-primary px-4 sm:px-6 py-2 sm:py-3 rounded-full text-xs sm:text-sm font-semibold mb-6 sm:mb-8 animate-fade-in border border-primary/25 backdrop-blur-xl shadow-lg">
                <div className="relative">
                  <div className="hidden sm:block"><AnimatedSanta size={36} /></div>
                  <div className="sm:hidden"><AnimatedSanta size={28} /></div>
                  <div className="absolute -top-1 -right-1 w-2 h-2 sm:w-3 sm:h-3 bg-accent rounded-full animate-pulse" />
                </div>
                <span className="text-foreground">The Complete Christmas Experience</span>
                <Sparkles className="w-3 h-3 sm:w-4 sm:h-4 text-gold animate-pulse" />
              </div>

              {/* Main Heading with Premium Typography */}
              <h1 className="text-4xl sm:text-5xl md:text-6xl lg:text-7xl font-bold font-serif text-foreground mb-6 sm:mb-8 animate-fade-in leading-tight" style={{ animationDelay: "0.1s" }}>
                Make Christmas
                <span className="text-gradient-premium block mt-1 sm:mt-2 pb-2 relative">
                  Truly Magical
                  <svg className="absolute -bottom-1 sm:-bottom-2 left-0 w-full h-2 sm:h-3 text-gold/30" viewBox="0 0 200 12" preserveAspectRatio="none">
                    <path d="M0 6 Q50 0 100 6 T200 6" fill="none" stroke="currentColor" strokeWidth="3" strokeLinecap="round"/>
                  </svg>
                </span>
              </h1>

              {/* Subheading */}
              <p className="text-base sm:text-lg md:text-xl text-muted-foreground mb-8 sm:mb-10 animate-fade-in leading-relaxed max-w-xl mx-auto lg:mx-0" style={{ animationDelay: "0.2s" }}>
                The ultimate Christmas companion for modern families. Let your children call Santa, track wishlists, plan Elf adventures, and create memories that last forever.
              </p>

              {/* CTA Buttons */}
              <div className="flex flex-col sm:flex-row gap-3 sm:gap-4 justify-center lg:justify-start mb-6 sm:mb-8 animate-fade-in" style={{ animationDelay: "0.3s" }}>
                <Link to="/auth?mode=signup">
                  <Button variant="christmas" size="lg" className="group w-full sm:w-auto shadow-xl hover:shadow-2xl transition-all text-sm sm:text-base">
                    <Gift className="w-4 h-4 sm:w-5 sm:h-5 group-hover:rotate-12 transition-transform" />
                    Get Started Free
                    <ArrowRight className="w-3 h-3 sm:w-4 sm:h-4 ml-1 group-hover:translate-x-1 transition-transform" />
                  </Button>
                </Link>
                <Link to="/pricing">
                  <Button variant="glass" size="lg" className="w-full sm:w-auto backdrop-blur-xl border-border/40 text-sm sm:text-base">
                    View Pricing
                  </Button>
                </Link>
              </div>
              
              <div className="flex flex-col sm:flex-row items-center gap-3 sm:gap-6 justify-center lg:justify-start animate-fade-in text-xs sm:text-sm" style={{ animationDelay: "0.4s" }}>
                <p className="text-muted-foreground flex items-center gap-2">
                  <Check className="w-3.5 h-3.5 sm:w-4 sm:h-4 text-accent" />
                  5 free features
                </p>
                <p className="text-muted-foreground flex items-center gap-2">
                  <Check className="w-3.5 h-3.5 sm:w-4 sm:h-4 text-accent" />
                  AI upgrades from £3.99
                </p>
              </div>
            </div>

            {/* Hero Right Side with Animated Elements */}
            <div className="relative mt-8 lg:mt-0">
              {/* Glowing backdrop */}
              <div className="absolute inset-0 bg-gradient-to-br from-primary/10 via-gold/5 to-accent/10 rounded-2xl sm:rounded-3xl blur-2xl sm:blur-3xl" />
              
              {/* Main content card with glassmorphism */}
              <div className="relative card-glass-dark rounded-2xl sm:rounded-3xl p-5 sm:p-8 space-y-6 sm:space-y-8 border-white/20">
                {/* Decorative corner elements - hidden on mobile */}
                <div className="absolute -top-3 -left-3 sm:-top-4 sm:-left-4 hidden sm:block">
                  <AnimatedStar size={40} />
                </div>
                <div className="absolute -bottom-3 -right-3 sm:-bottom-4 sm:-right-4 hidden sm:block">
                  <AnimatedGift size={45} />
                </div>
                
                {/* Countdown */}
                <div className="text-center relative z-10">
                  <p className="text-[10px] sm:text-xs text-muted-foreground mb-4 sm:mb-5 uppercase tracking-[0.15em] sm:tracking-[0.2em] font-medium">Days Until Christmas</p>
                  <CountdownTimer />
                </div>
                
                {/* Call Preview with enhanced styling */}
                <div className="relative z-10">
                  <CallPreview />
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Trust Badges */}
      <section className="relative z-10 py-8 sm:py-12 px-4">
        <div className="container max-w-4xl mx-auto">
          <TrustBadges />
        </div>
      </section>

      {/* Video Showcase Section */}
      <section className="relative z-10 py-16 sm:py-24 px-4 bg-gradient-to-b from-transparent via-secondary/30 to-transparent">
        <div className="container max-w-5xl mx-auto">
          <div className="grid lg:grid-cols-2 gap-8 lg:gap-12 items-center">
            {/* Video Preview */}
            <div className="relative order-2 lg:order-1">
              <div className="absolute inset-0 bg-gradient-to-br from-primary/10 via-gold/5 to-accent/10 rounded-2xl blur-2xl" />
              <div className="relative card-glass-dark rounded-2xl p-4 sm:p-6 border-white/20">
                <div className="flex items-center gap-2 mb-4">
                  <div className="w-8 h-8 rounded-lg bg-gradient-to-br from-primary/20 to-gold/10 flex items-center justify-center">
                    <Video className="w-4 h-4 text-primary" />
                  </div>
                  <div>
                    <h4 className="text-sm font-semibold text-foreground">Example Santa Video</h4>
                    <p className="text-[10px] text-muted-foreground">See the magic in action</p>
                  </div>
                </div>
                <VideoPreview />
              </div>
            </div>

            {/* Content */}
            <div className="order-1 lg:order-2 text-center lg:text-left">
              <p className="text-[10px] sm:text-xs uppercase tracking-[0.15em] sm:tracking-[0.2em] text-primary font-semibold mb-3 sm:mb-4">New Feature</p>
              <h2 className="text-3xl sm:text-4xl md:text-5xl font-bold font-serif text-foreground mb-4 sm:mb-6">
                Personalised <span className="text-gradient-gold">Santa Videos</span>
              </h2>
              <p className="text-muted-foreground text-sm sm:text-lg mb-6 leading-relaxed">
                Watch your child's face light up when Santa calls them by name! Our AI creates personalised video messages mentioning their friends, school, and favourite things.
              </p>
              
              <ul className="space-y-3 mb-8 text-left max-w-md mx-auto lg:mx-0">
                {[
                  "Santa mentions your child by name",
                  "Includes their friends, school & hobbies",
                  "New video topics available daily",
                  "Download & share with family"
                ].map((item) => (
                  <li key={item} className="flex items-center gap-3 text-sm text-muted-foreground">
                    <div className="w-5 h-5 rounded-full bg-accent/20 flex items-center justify-center flex-shrink-0">
                      <Check className="w-3 h-3 text-accent" />
                    </div>
                    {item}
                  </li>
                ))}
              </ul>

              <div className="flex flex-col sm:flex-row gap-3 justify-center lg:justify-start">
                <Link to="/auth?mode=signup">
                  <Button variant="christmas" size="lg" className="w-full sm:w-auto group">
                    <Play className="w-4 h-4 group-hover:scale-110 transition-transform" />
                    Try Free for 15 Minutes
                    <ArrowRight className="w-4 h-4 ml-1 group-hover:translate-x-1 transition-transform" />
                  </Button>
                </Link>
                <Link to="/pricing">
                  <Button variant="glass" size="lg" className="w-full sm:w-auto">
                    View Pricing
                  </Button>
                </Link>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Why Parents Love It Section */}
      <section id="about" className="relative z-10 py-16 sm:py-24 px-4">
        <div className="container max-w-6xl mx-auto">
          <div className="text-center mb-10 sm:mb-16">
            <p className="text-[10px] sm:text-xs uppercase tracking-[0.15em] sm:tracking-[0.2em] text-primary font-semibold mb-3 sm:mb-4">Why Santa Pass</p>
            <h2 className="text-3xl sm:text-4xl md:text-5xl font-bold font-serif text-foreground mb-4 sm:mb-6">
              Why Families <span className="text-gradient-gold">Love Us</span>
            </h2>
            <p className="text-muted-foreground text-sm sm:text-lg max-w-2xl mx-auto leading-relaxed px-4">
              We built this for real parents who want to create Christmas magic without the stress
            </p>
          </div>

          <div className="grid sm:grid-cols-2 md:grid-cols-3 gap-4 sm:gap-8">
            {[
              {
                icon: Layers,
                title: "All-in-One Hub",
                description: "No more juggling apps! Wishlists, Elf ideas, Santa letters, Secret Santa draws — everything organized in one festive dashboard."
              },
              {
                icon: Wand2,
                title: "Keep the Magic Alive",
                description: "Children can call Santa and hear his voice, send real letters and get magical replies, earn Nice List certificates, and see their good deeds tracked!"
              },
              {
                icon: Clock,
                title: "Stress-Free Planning",
                description: "Track what gifts to buy, plan a month of Elf on the Shelf ideas, organize Secret Santa draws, and never forget who wanted what."
              }
            ].map((item, index) => (
              <Card 
                key={item.title}
                className="card-glass-dark border-border/30 text-center p-5 sm:p-8 group hover:-translate-y-2 transition-all duration-500"
                style={{ animationDelay: `${index * 100}ms` }}
              >
                <CardContent className="pt-2">
                  <div className="w-12 h-12 sm:w-16 sm:h-16 mx-auto mb-4 sm:mb-6 rounded-xl sm:rounded-2xl bg-gradient-to-br from-primary/15 to-gold/10 flex items-center justify-center group-hover:scale-110 transition-transform duration-500 border border-primary/20">
                    <item.icon className="w-6 h-6 sm:w-8 sm:h-8 text-primary" />
                  </div>
                  <h3 className="font-serif font-bold text-lg sm:text-xl text-foreground mb-3 sm:mb-4">{item.title}</h3>
                  <p className="text-sm sm:text-base text-muted-foreground leading-relaxed">{item.description}</p>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* How It Works Section */}
      <section id="how-it-works" className="relative z-10 py-16 sm:py-24 px-4 bg-gradient-to-b from-secondary/20 via-secondary/40 to-secondary/20">
        <div className="container max-w-5xl mx-auto">
          <div className="text-center mb-10 sm:mb-16">
            <p className="text-[10px] sm:text-xs uppercase tracking-[0.15em] sm:tracking-[0.2em] text-primary font-semibold mb-3 sm:mb-4">Getting Started</p>
            <h2 className="text-3xl sm:text-4xl md:text-5xl font-bold font-serif text-foreground mb-4 sm:mb-6">
              How It Works
            </h2>
            <p className="text-muted-foreground text-sm sm:text-lg">
              Get started in seconds and let the Christmas magic begin
            </p>
          </div>

          <div className="grid grid-cols-2 md:grid-cols-4 gap-4 sm:gap-8">
            {[
              { num: "1", title: "Create Free Account", desc: "Sign up in seconds — no credit card needed" },
              { num: "2", title: "Add Your Children", desc: "Set up profiles for each child to personalize" },
              { num: "3", title: "Explore Free Features", desc: "Use wishlists, Secret Santa, and more" },
              { num: "4", title: "Upgrade for Magic", desc: "Add AI features from just £3.99", accent: true },
            ].map((step, index) => (
              <div key={step.num} className="text-center group" style={{ animationDelay: `${index * 100}ms` }}>
                <div className={`w-14 h-14 sm:w-20 sm:h-20 mx-auto mb-4 sm:mb-6 rounded-2xl sm:rounded-3xl flex items-center justify-center text-xl sm:text-3xl font-bold font-serif transition-all duration-500 group-hover:scale-110 group-hover:-translate-y-2 backdrop-blur-sm ${
                  step.accent 
                    ? 'bg-gradient-gold text-gold-foreground shadow-gold border border-gold/30' 
                    : 'bg-card/25 backdrop-blur-sm border border-border/30 text-primary shadow-card'
                }`}>
                  {step.num}
                </div>
                <h3 className="font-semibold text-foreground mb-2 sm:mb-3 text-sm sm:text-lg">{step.title}</h3>
                <p className="text-xs sm:text-sm text-muted-foreground leading-relaxed">{step.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Features Section */}
      <section id="features" className="relative z-10 py-16 sm:py-24 px-4">
        <div className="container max-w-6xl mx-auto">
          <div className="text-center mb-10 sm:mb-16">
            <p className="text-[10px] sm:text-xs uppercase tracking-[0.15em] sm:tracking-[0.2em] text-primary font-semibold mb-3 sm:mb-4">Features</p>
            <h2 className="text-3xl sm:text-4xl md:text-5xl font-bold font-serif text-foreground mb-4 sm:mb-6">
              Everything You Need for
              <span className="text-gradient-gold block mt-1 sm:mt-2">A Perfect Christmas</span>
            </h2>
            <p className="text-muted-foreground text-sm sm:text-lg max-w-2xl mx-auto px-4">
              Start with powerful free features, then unlock AI magic when you're ready
            </p>
          </div>

          {/* Free Features */}
          <div className="mb-12 sm:mb-20">
            <div className="flex items-center gap-2 sm:gap-3 mb-6 sm:mb-8">
              <div className="h-px flex-1 bg-gradient-to-r from-transparent via-border to-transparent" />
              <span className="text-xs sm:text-sm font-semibold text-accent uppercase tracking-wider px-3 sm:px-4 py-1.5 sm:py-2 bg-accent/10 rounded-full border border-accent/20 backdrop-blur-sm">Free Features</span>
              <div className="h-px flex-1 bg-gradient-to-r from-transparent via-border to-transparent" />
            </div>

            <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 sm:gap-6">
              {freeFeatures.map((feature, index) => (
                <Card 
                  key={feature.title} 
                  className="card-glass-dark border-border/30 relative overflow-hidden group hover:-translate-y-2"
                  style={{ animationDelay: `${index * 50}ms` }}
                >
                  <div className="absolute top-2 right-2 sm:top-4 sm:right-4 bg-accent/20 text-accent text-[9px] sm:text-2xs px-1.5 sm:px-2.5 py-0.5 sm:py-1 rounded-full font-semibold uppercase tracking-wider border border-accent/30">
                    Free
                  </div>
                  <CardContent className="p-4 sm:p-6 text-center">
                    <div className="w-10 h-10 sm:w-14 sm:h-14 mx-auto mb-3 sm:mb-4 rounded-xl sm:rounded-2xl flex items-center justify-center bg-gradient-to-br from-accent/15 to-accent/5 text-accent group-hover:scale-110 transition-all duration-300 border border-accent/20">
                      <feature.icon className="w-5 h-5 sm:w-7 sm:h-7" />
                    </div>
                    <h3 className="font-semibold text-foreground mb-1 sm:mb-2 text-sm sm:text-base">{feature.title}</h3>
                    <p className="text-xs sm:text-sm text-muted-foreground leading-relaxed">{feature.description}</p>
                  </CardContent>
                </Card>
              ))}
            </div>
          </div>

          {/* Premium Features */}
          <div>
            <div className="flex items-center gap-2 sm:gap-3 mb-6 sm:mb-8">
              <div className="h-px flex-1 bg-gradient-to-r from-transparent via-gold/50 to-transparent" />
              <span className="text-xs sm:text-sm font-semibold text-gold uppercase tracking-wider px-3 sm:px-4 py-1.5 sm:py-2 bg-gold/10 rounded-full border border-gold/20 backdrop-blur-sm">Premium AI Features</span>
              <div className="h-px flex-1 bg-gradient-to-r from-transparent via-gold/50 to-transparent" />
            </div>

            <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-3 sm:gap-6">
              {ultimatePassFeatures.map((feature, index) => {
                const isHighlight = 'highlight' in feature && feature.highlight;
                return (
                  <Card 
                    key={feature.title} 
                    className={`card-glass-dark border-border/30 relative overflow-hidden group hover:-translate-y-2 ${
                      isHighlight ? 'ring-2 ring-primary/40' : ''
                    }`}
                    style={{ animationDelay: `${index * 50}ms` }}
                  >
                    <div className={`absolute top-2 right-2 sm:top-4 sm:right-4 text-[9px] sm:text-2xs px-1.5 sm:px-2.5 py-0.5 sm:py-1 rounded-full font-semibold uppercase tracking-wider ${
                      isHighlight ? 'bg-primary/20 text-primary border border-primary/30' : 'bg-gold/20 text-gold border border-gold/30'
                    }`}>
                      {isHighlight ? 'Ultimate' : 'Magic+'}
                    </div>
                    <CardContent className="p-4 sm:p-6 text-center">
                      <div className={`w-10 h-10 sm:w-14 sm:h-14 mx-auto mb-3 sm:mb-4 rounded-xl sm:rounded-2xl flex items-center justify-center group-hover:scale-110 transition-all duration-300 ${
                        isHighlight 
                          ? 'bg-gradient-to-br from-primary/20 to-primary/5 text-primary border border-primary/20' 
                          : 'bg-gradient-to-br from-gold/15 to-gold/5 text-gold border border-gold/20'
                      }`}>
                        <feature.icon className="w-5 h-5 sm:w-7 sm:h-7" />
                      </div>
                      <h3 className="font-semibold text-foreground mb-1 sm:mb-2 text-sm sm:text-base">{feature.title}</h3>
                      <p className="text-xs sm:text-sm text-muted-foreground leading-relaxed">{feature.description}</p>
                    </CardContent>
                  </Card>
                );
              })}
            </div>
          </div>
        </div>
      </section>

      {/* Pricing Section */}
      <section id="pricing" className="relative z-10 py-16 sm:py-24 px-4 bg-gradient-to-b from-transparent via-secondary/20 to-transparent">
        <div className="container max-w-5xl mx-auto">
          <div className="text-center mb-10 sm:mb-16">
            <p className="text-[10px] sm:text-xs uppercase tracking-[0.15em] sm:tracking-[0.2em] text-primary font-semibold mb-3 sm:mb-4">Pricing</p>
            <h2 className="text-3xl sm:text-4xl md:text-5xl font-bold font-serif text-foreground mb-4 sm:mb-6">
              Choose Your <span className="text-gradient-red">Christmas Experience</span>
            </h2>
            <p className="text-muted-foreground text-sm sm:text-lg max-w-2xl mx-auto px-4">
              Start free, upgrade when you want more magic. No subscriptions, just simple one-time payments.
            </p>
          </div>

          <div className="grid sm:grid-cols-2 md:grid-cols-3 gap-4 sm:gap-6">
            {pricingTiers.map((tier, index) => (
              <Card 
                key={tier.name} 
                className={`relative overflow-hidden transition-all duration-500 hover:-translate-y-2 ${
                  tier.highlight 
                    ? 'card-glass-dark border-primary/30 ring-2 ring-primary/30' 
                    : tier.popular 
                      ? 'card-glass-dark border-gold/30 ring-2 ring-gold/30' 
                      : 'card-glass-dark border-border/30'
                }`}
                style={{ animationDelay: `${index * 100}ms` }}
              >
                {tier.popular && (
                  <div className="absolute top-0 left-0 right-0 bg-gradient-gold text-gold-foreground text-[10px] sm:text-xs py-2 sm:py-2.5 text-center font-bold uppercase tracking-wider">
                    Most Popular
                  </div>
                )}
                {tier.highlight && !tier.popular && (
                  <div className="absolute top-0 left-0 right-0 bg-gradient-red text-primary-foreground text-[10px] sm:text-xs py-2 sm:py-2.5 text-center font-bold uppercase tracking-wider">
                    Best Value
                  </div>
                )}
                <CardContent className={`p-5 sm:p-8 text-center ${tier.popular || tier.highlight ? 'pt-12 sm:pt-16' : ''}`}>
                  <h3 className="font-serif font-bold text-lg sm:text-xl text-foreground mb-2 sm:mb-3">{tier.name}</h3>
                  <div className="text-4xl sm:text-5xl font-bold text-foreground font-serif mb-1 sm:mb-2">{tier.price}</div>
                  <p className="text-xs sm:text-sm text-muted-foreground mb-1 sm:mb-2">{tier.duration}</p>
                  <p className="text-xs sm:text-sm text-muted-foreground mb-6 sm:mb-8">{tier.description}</p>
                  
                  <ul className="text-left space-y-2 sm:space-y-3 mb-6 sm:mb-8">
                    {tier.features.map((feature) => (
                      <li key={feature} className="flex items-start gap-2 sm:gap-3 text-xs sm:text-sm">
                        <Check className={`w-4 h-4 sm:w-5 sm:h-5 flex-shrink-0 mt-0.5 ${
                          tier.highlight ? 'text-primary' : tier.popular ? 'text-gold' : 'text-accent'
                        }`} />
                        <span className="text-muted-foreground">{feature}</span>
                      </li>
                    ))}
                  </ul>

                  <Link to={"/auth?mode=signup"} className="block">
                    <Button 
                      variant={tier.highlight ? "christmas" : tier.popular ? "gold" : "glass"} 
                      className="w-full text-sm sm:text-base"
                      size="lg"
                    >
                      <Gift className="w-4 h-4 sm:w-5 sm:h-5" />
                      {tier.tier === "free" ? "Start Free" : "Get Pass"}
                    </Button>
                  </Link>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* Social Proof */}
      <section className="relative z-10 py-16 sm:py-24 px-4">
        <div className="container max-w-4xl mx-auto">
          <div className="text-center mb-8 sm:mb-12">
            <p className="text-[10px] sm:text-xs uppercase tracking-[0.15em] sm:tracking-[0.2em] text-primary font-semibold mb-3 sm:mb-4">Testimonials</p>
            <h2 className="text-3xl sm:text-4xl md:text-5xl font-bold font-serif text-foreground">
              What Parents Are Saying
            </h2>
          </div>
          <SocialProof />
        </div>
      </section>

      {/* Support Section */}
      <section id="support" className="relative z-10 py-16 sm:py-24 px-4 bg-gradient-to-b from-secondary/20 via-secondary/40 to-secondary/20">
        <div className="container max-w-4xl mx-auto">
          <div className="text-center mb-8 sm:mb-12">
            <p className="text-[10px] sm:text-xs uppercase tracking-[0.15em] sm:tracking-[0.2em] text-primary font-semibold mb-3 sm:mb-4">Support</p>
            <h2 className="text-3xl sm:text-4xl md:text-5xl font-bold font-serif text-foreground mb-4 sm:mb-6">
              Need <span className="text-gradient-gold">Help?</span>
            </h2>
            <p className="text-muted-foreground text-sm sm:text-lg">
              We're here to make your Christmas experience magical
            </p>
          </div>

          <div className="grid sm:grid-cols-2 gap-4 sm:gap-8">
            <Card className="card-glass-dark border-border/30 p-5 sm:p-8 group hover:-translate-y-2 transition-all duration-500">
              <CardContent className="pt-2 text-center">
                <div className="w-12 h-12 sm:w-16 sm:h-16 mx-auto mb-4 sm:mb-6 rounded-xl sm:rounded-2xl bg-gradient-to-br from-primary/15 to-primary/5 flex items-center justify-center group-hover:scale-110 transition-transform duration-300 border border-primary/20">
                  <HelpCircle className="w-6 h-6 sm:w-8 sm:h-8 text-primary" />
                </div>
                <h3 className="font-serif font-bold text-lg sm:text-xl text-foreground mb-3 sm:mb-4">FAQs</h3>
                <p className="text-sm sm:text-base text-muted-foreground mb-4 sm:mb-6 leading-relaxed">
                  Find answers to common questions about Santa Pass, payments, and features.
                </p>
                <Link to="/pricing">
                  <Button variant="glass" size="lg" className="text-sm sm:text-base">View FAQs</Button>
                </Link>
              </CardContent>
            </Card>

            <Card className="card-glass-dark border-border/30 p-5 sm:p-8 group hover:-translate-y-2 transition-all duration-500">
              <CardContent className="pt-2 text-center">
                <div className="w-12 h-12 sm:w-16 sm:h-16 mx-auto mb-4 sm:mb-6 rounded-xl sm:rounded-2xl bg-gradient-to-br from-accent/15 to-accent/5 flex items-center justify-center group-hover:scale-110 transition-transform duration-300 border border-accent/20">
                  <Heart className="w-6 h-6 sm:w-8 sm:h-8 text-accent" />
                </div>
                <h3 className="font-serif font-bold text-lg sm:text-xl text-foreground mb-3 sm:mb-4">Contact Us</h3>
                <p className="text-sm sm:text-base text-muted-foreground mb-4 sm:mb-6 leading-relaxed">
                  Have a question? Our friendly team is here to help make your Christmas special.
                </p>
                <Button variant="glass" size="lg" className="text-sm sm:text-base">Get in Touch</Button>
              </CardContent>
            </Card>
          </div>
        </div>
      </section>

      {/* Final CTA */}
      <section className="relative z-10 py-16 sm:py-24 px-4">
        <div className="container max-w-3xl mx-auto text-center">
          <div className="card-glass-dark rounded-2xl sm:rounded-3xl p-6 sm:p-10 md:p-16 relative overflow-hidden">
            {/* Decorative gradients */}
            <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_top_left,hsl(348_70%_38%/0.1),transparent_50%)]" />
            <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_bottom_right,hsl(42_75%_48%/0.1),transparent_50%)]" />
            
            {/* Decorative elements - hidden on mobile */}
            <Sparkles className="hidden sm:block absolute top-8 left-8 w-6 h-6 text-gold/30 animate-sparkle" />
            <Sparkles className="hidden sm:block absolute top-12 right-12 w-4 h-4 text-primary/30 animate-sparkle" style={{ animationDelay: "0.5s" }} />
            <Sparkles className="hidden sm:block absolute bottom-8 left-16 w-5 h-5 text-accent/30 animate-sparkle" style={{ animationDelay: "1s" }} />
            
            <div className="relative">
              <div className="w-16 h-16 sm:w-20 sm:h-20 mx-auto mb-6 sm:mb-8 rounded-full bg-gradient-to-br from-primary/20 to-gold/15 flex items-center justify-center border border-primary/20">
                <Gift className="w-8 h-8 sm:w-10 sm:h-10 text-primary" />
              </div>
              <h2 className="text-3xl sm:text-4xl md:text-5xl font-bold font-serif text-foreground mb-4 sm:mb-6">
                Ready to Make Christmas
                <span className="text-gradient-premium block mt-1 sm:mt-2">Unforgettable?</span>
              </h2>
              <p className="text-sm sm:text-lg text-muted-foreground mb-8 sm:mb-10 max-w-xl mx-auto leading-relaxed px-4">
                Join thousands of families creating magical Christmas memories. Start with our free features today!
              </p>
              
              <Link to="/auth">
                <Button variant="christmas" size="lg" className="animate-pulse-glow text-sm sm:text-base">
                  <Gift className="w-4 h-4 sm:w-5 sm:h-5" />
                  Start Creating Magic — Free
                  <ArrowRight className="w-3 h-3 sm:w-4 sm:h-4 ml-1" />
                </Button>
              </Link>

              <p className="text-xs sm:text-sm text-muted-foreground mt-6 sm:mt-8 flex items-center gap-2 justify-center">
                <Users className="w-3.5 h-3.5 sm:w-4 sm:h-4" />
                Join 10,000+ happy families this Christmas
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="relative z-10 py-12 sm:py-16 px-4 border-t border-border/30 bg-secondary/10">
        <div className="container max-w-6xl mx-auto">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-8 sm:gap-12 mb-8 sm:mb-12">
            <div className="col-span-2 md:col-span-1">
              <div className="flex items-center gap-2 sm:gap-2.5 mb-4 sm:mb-5">
                <TreePine className="w-6 h-6 sm:w-7 sm:h-7 text-accent" />
                <span className="font-serif font-bold text-lg sm:text-xl text-foreground">Santa Pass</span>
              </div>
              <p className="text-xs sm:text-sm text-muted-foreground leading-relaxed">
                Making Christmas magical for families around the world with AI-powered Santa calls and festive features.
              </p>
            </div>
            <div>
              <h4 className="font-semibold text-foreground mb-4 sm:mb-5 text-xs sm:text-sm uppercase tracking-wider">Free Features</h4>
              <ul className="space-y-2 sm:space-y-3 text-xs sm:text-sm text-muted-foreground">
                <li className="hover:text-foreground transition-colors cursor-pointer">Wishlist Builder</li>
                <li className="hover:text-foreground transition-colors cursor-pointer">Secret Santa</li>
                <li className="hover:text-foreground transition-colors cursor-pointer">Naughty & Nice</li>
                <li className="hover:text-foreground transition-colors cursor-pointer">Certificates</li>
              </ul>
            </div>
            <div>
              <h4 className="font-semibold text-foreground mb-4 sm:mb-5 text-xs sm:text-sm uppercase tracking-wider">Premium Features</h4>
              <ul className="space-y-2 sm:space-y-3 text-xs sm:text-sm text-muted-foreground">
                <li className="hover:text-foreground transition-colors cursor-pointer">Call Santa AI</li>
                <li className="hover:text-foreground transition-colors cursor-pointer">Elf Planner</li>
                <li className="hover:text-foreground transition-colors cursor-pointer">Christmas Stories</li>
                <li className="hover:text-foreground transition-colors cursor-pointer">Photo Creator</li>
              </ul>
            </div>
            <div>
              <h4 className="font-semibold text-foreground mb-4 sm:mb-5 text-xs sm:text-sm uppercase tracking-wider">Support</h4>
              <ul className="space-y-2 sm:space-y-3 text-xs sm:text-sm text-muted-foreground">
                <li>
                  <Link to="/contact" className="hover:text-foreground transition-colors">Contact Us</Link>
                </li>
                <li>
                  <Link to="/terms" className="hover:text-foreground transition-colors">Terms & Conditions</Link>
                </li>
                <li>
                  <Link to="/auth?mode=signup" className="hover:text-foreground transition-colors">Create Account</Link>
                </li>
                <li>
                  <Link to="/auth?mode=login" className="hover:text-foreground transition-colors">Login</Link>
                </li>
              </ul>
            </div>
          </div>
          
          <div className="pt-6 sm:pt-8 border-t border-border/30 text-center">
            <p className="text-xs sm:text-sm text-muted-foreground flex items-center justify-center gap-2">
              © {new Date().getFullYear()} Santa Pass. Made with <Heart className="w-3.5 h-3.5 sm:w-4 sm:h-4 text-primary" /> for families everywhere.
            </p>
            <p className="text-[10px] sm:text-xs text-muted-foreground mt-1.5 sm:mt-2">
              Payments securely processed by Stripe.
            </p>
          </div>
        </div>
      </footer>
      </main>
    </div>
  );
};

export default Index;
